/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type {
  PostgrestMaybeSingleResponse,
  PostgrestResponse,
  PostgrestSingleResponse,
} from "@supabase/postgrest-js";
import type { Database } from "../../../__definitions__/database-definitions";
import {
  BaseSupabaseService,
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../utils/uuid";
import type { SlackManifest } from "../../integrations/types";

export type CustomEmoji = Database["public"]["Tables"]["custom_emoji"]["Row"];
export type SlackAlias = Database["public"]["Tables"]["slack_alias"]["Row"];
export type SlackAliasWithDefault = Database["public"]["Views"]["slack_alias_with_default"]["Row"];
type SlackAliasDefault = Database["public"]["Tables"]["slack_alias_default"]["Row"];

export class SupabaseSlackAliasWithDefaultService extends SupabaseServiceView<"slack_alias_with_default"> {
  table = "slack_alias_with_default" as const;

  async setDefault(alias_id: UUID | null): Promise<PostgrestResponse<SlackAliasWithDefault>> {
    if (alias_id)
      return this.log_errors(await this.client.rpc("slack_alias_set_default", { alias_id }));

    return this.log_errors(await this.client.rpc("slack_alias_remove_default"));
  }
}

export class SupabaseSlackAliasService extends SupabaseService<"slack_alias"> {
  table = "slack_alias" as const;
}

export class SupabaseSlackAliasDefaultService extends SupabaseService<"slack_alias_default"> {
  table = "slack_alias_default" as const;

  async getSingle(): Promise<PostgrestMaybeSingleResponse<SlackAliasDefault>> {
    return this.log_errors(await this.client.from(this.table).select().single());
  }
}

export class SupabaseSuperAdminSlackService extends BaseSupabaseService<"super_admin"> {
  async createSlackManifest(
    manifest: SlackManifest,
  ): Promise<
    PostgrestSingleResponse<
      Database["super_admin"]["Functions"]["create_slack_manifest"]["Returns"]
    >
  > {
    return await this.client.rpc("create_slack_manifest", { manifest });
  }

  async updateSlackManifest(
    manifest: SlackManifest,
    app_id: string,
  ): Promise<
    PostgrestSingleResponse<
      Database["super_admin"]["Functions"]["update_slack_manifest"]["Returns"]
    >
  > {
    return await this.client.rpc("update_slack_manifest", { manifest, app_id });
  }
}

export class SupabaseCustomEmojiService extends SupabaseService<"custom_emoji"> {
  table = "custom_emoji" as const;

  async getByName(
    account_id: UUID,
    name: string,
  ): Promise<PostgrestMaybeSingleResponse<CustomEmoji>> {
    return this.log_errors(
      await this.client
        .from(this.table)
        .select()
        .eq("account_id", account_id)
        .eq("emoji", name)
        .maybeSingle(),
    );
  }

  async getByNames(account_id: UUID, names: string[]): Promise<PostgrestResponse<CustomEmoji>> {
    return this.log_errors(
      await this.client.from(this.table).select().eq("account_id", account_id).in("emoji", names),
    );
  }
}

export class SupabaseSlackAppService extends SupabaseServiceView<"slack_app_config"> {
  table = "slack_app_config" as const;

  async videoEnabled(account_id: UUID) {
    const select = this.client.from(this.table).select("*").in("version", ["4", "5"]);
    return this.log_errors(
      ((await this.getAllFromSelection(account_id, select, { order: [] }))?.data?.length ?? 0) > 0,
    );
  }
}
